/**
 * Applies a small position offset (jitter) to data points that share the same
 * (xKey, yKey) coordinates, so they remain visually distinguishable on a scatter chart.
 *
 * Points with unique coordinates are not moved.
 * Points with null/undefined coordinates are passed through unchanged.
 * When N points overlap, they are spread evenly in a small circle around the original point.
 *
 * @param {Array} data - array of data objects
 * @param {string} xKey - key for the x-axis value
 * @param {string} yKey - key for the y-axis value
 * @param {number} [xJitter=8] - offset radius in x-axis units
 * @param {number[]} [xRange=[0,700]] - [min, max] of the x axis (used to scale y jitter proportionally)
 * @param {number[]} [yRange=[0,10]] - [min, max] of the y axis
 * @returns {Array} new array with jitter applied (originals unchanged)
 */
export const applyJitter = (data, xKey, yKey, xJitter = 8, xRange = [0, 700], yRange = [0, 10]) => {
    if (!data || data.length === 0) return data;

    // Scale y-jitter proportionally to the axis ranges so the visual offset looks balanced
    const axisRatio = (xRange[1] - xRange[0]) / (yRange[1] - yRange[0]);
    const yJitter = xJitter / axisRatio;

    // Group indices by their (x, y) coordinate key; skip points with missing coordinates
    const groups = {};
    data.forEach((point, idx) => {
        const x = point[xKey];
        const y = point[yKey];
        if (x == null || y == null) return; // pass through unchanged
        const key = `${x},${y}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(idx);
    });

    const result = data.map((point) => ({ ...point }));

    Object.values(groups).forEach((indices) => {
        if (indices.length <= 1) return; // unique position — no jitter needed

        // Spread overlapping points evenly around a circle
        indices.forEach((idx, i) => {
            const angle = (2 * Math.PI * i) / indices.length;
            result[idx][xKey] = data[idx][xKey] + xJitter * Math.cos(angle);
            result[idx][yKey] = data[idx][yKey] + yJitter * Math.sin(angle);
        });
    });

    return result;
};
